/**
 * Faux Embroidery Generator
 * Shopify → Lambda → S3 → Cutout.Pro → proportional size pricing → cart
 *
 * Pricing:
 * - Faux Embroidery DTF Transfer: $0.03 / sq inch (max 22" × 22")
 * - Faux Embroidery 3D Patch: $0.45 / sq inch (min 1", max 10" × 10")
 *
 * Quantity: minimum 10 pcs (DTF) / 20 pcs (3D Patch)
 * DTF minimum order: $10 subtotal (before discount); volume discounts unlock after that
 * Volume discounts applied by piece count (see product config)
 * Size: locked to the artwork after background removal. Crop can change that area.
 *
 * Cart charge: variant should be $0.01; quantity = round(lineTotal * 100)
 */
(function () {
  // Single long attempt. Progress UI targets ~30–60s typical Cutout time.
  const GENERATION_TIMEOUT_MS = 240000;
  const UPLOAD_MAX_EDGE = 1600;
  const SIZE_STEP = 0.25;
  const DEFAULT_MAX_PREVIEWS_PER_DAY = 4;
  const PREVIEW_STORAGE_PREFIX = 'dtf_embroidery_previews_';
  const GUEST_ID_KEY = 'dtf_embroidery_guest_id';
  const QUOTA_TZ = 'America/Los_Angeles';
  // Near-white pixels at or above this RGB value become transparent
  const WHITE_BG_THRESHOLD = 245;
  // Dark fabric plate around Cutout "Retro Badge" style outputs
  const FABRIC_LUMA_MAX = 115;
  const FABRIC_COLOR_DIST = 105;
  // Worker cleanup max side — off main thread, safe for UI
  const CLEANUP_MAX_SIDE = 720;
  const PREVIEW_PROXY_TIMEOUT_MS = 45000;

  class EmbroideryGenerator {
    constructor(root) {
      this.root = root;
      this.lambdaUrl = (root.dataset.lambdaUrl || '').replace(/\/$/, '');
      this.productFormId = root.dataset.productFormId || '';
      this.productHandle = root.dataset.productHandle || '';
      this.rate = parseFloat(root.dataset.priceRate || '0.45') || 0.45;
      this.minW = parseFloat(root.dataset.minWidth || '0.25') || 0.25;
      this.minH = parseFloat(root.dataset.minHeight || '0.25') || 0.25;
      this.maxW = parseFloat(root.dataset.maxWidth || '22') || 22;
      this.maxH = parseFloat(root.dataset.maxHeight || '22') || 22;
      this.minQty = parseInt(root.dataset.minQty || '20', 10) || 20;
      this.minOrderAmount = parseFloat(root.dataset.minOrder || '0') || 0;
      this.volumeDiscountTiers = EmbroideryGenerator.parseVolumeDiscountTiers(
        root.dataset.volumeDiscounts
      );
      this.customerId = (root.dataset.customerId || '').trim();
      this.dailyLimitEnabled = root.dataset.dailyLimitEnabled !== 'false';
      this.maxPreviewsPerDay =
        parseInt(root.dataset.dailyGenerateLimit || String(DEFAULT_MAX_PREVIEWS_PER_DAY), 10) ||
        DEFAULT_MAX_PREVIEWS_PER_DAY;
      this.s3PublicBase = (root.dataset.s3PublicBase || '').replace(/\/$/, '');
      // "cents" = legacy $0.01×cents qty | "function" = real pieces + Cart Transform app
      this.pricingMode = (root.dataset.pricingMode || 'cents').toLowerCase();
      this.variantAvailable = root.dataset.variantAvailable === 'true';
      this.sectionId = root.dataset.sectionId || '';

      this.fileInput = root.querySelector('[data-embroidery-file]');
      this.generateBtn = root.querySelector('[data-embroidery-generate]');
      this.cropBtn = root.querySelector('[data-embroidery-crop]');
      this.statusEl = root.querySelector('[data-embroidery-status]');
      this.originalCard = root.querySelector('[data-embroidery-original-card]');
      this.generatedCard = root.querySelector('[data-embroidery-generated-card]');
      this.originalImg = root.querySelector('[data-embroidery-original-img]');
      this.generatedImg = root.querySelector('[data-embroidery-generated-img]');
      this.sizePanel = root.querySelector('[data-embroidery-size-panel]');
      this.widthInput = root.querySelector('[data-embroidery-width]');
      this.heightInput = root.querySelector('[data-embroidery-height]');
      this.sqinEl = root.querySelector('[data-embroidery-sqin]');
      this.unitPriceEl = root.querySelector('[data-embroidery-unit-price]');
      this.discountRowEl = root.querySelector('[data-embroidery-discount-row]');
      this.discountEl = root.querySelector('[data-embroidery-discount]');
      this.lineTotalEl = root.querySelector('[data-embroidery-line-total]');
      this.qtyDiscountsPanel = root.querySelector('[data-embroidery-qty-discounts]');
      this.qtyDiscountGateEl = root.querySelector('[data-embroidery-discount-gate]');
      this.qtyDiscountListEl = root.querySelector('[data-embroidery-discount-list]');
      this.progressEl = root.querySelector('[data-embroidery-progress]');
      this.progressLabelEl = root.querySelector('[data-embroidery-progress-label]');
      this.progressFillEl = root.querySelector('[data-embroidery-progress-fill]');
      this.progressPctEl = root.querySelector('[data-embroidery-progress-pct]');
      this.progressElapsedEl = root.querySelector('[data-embroidery-progress-elapsed]');
      this.progressCancelBtn = root.querySelector('[data-embroidery-progress-cancel]');
      this.progressTimer = null;
      this.elapsedTimer = null;
      this.progressValue = 0;
      this.progressStartedAt = 0;
      this.activeAbortController = null;
      this.userCancelled = false;

      this.originalKey = null;
      this.originalImageUrl = null;
      this.generatedImageUrl = null; // cleaned transparent PNG URL for fulfillment
      this.cutoutSourceUrl = null; // raw Cutout output (may include fabric)
      this.fulfillmentImageUrl = null;
      this.generatedKey = null;
      this.cutoutId = null;
      this.selectedFile = null;
      this.isGenerating = false;
      this.hasGenerated = false;
      this.aspectRatio = null; // width / height of the artwork after background removal
      this.imagePixelWidth = null;
      this.imagePixelHeight = null;
      this.sizeUsesArtwork = false;
      this.artworkImage = null;
      this.trimmedFulfillmentUrl = '';
      this.isSyncingProportion = false;

      this.pieceQtyInput = null;
      this.cartQtyInput = null;
      this.imageDimsEl = root.querySelector('[data-embroidery-image-dims]');

      this.init();
    }

    static parseVolumeDiscountTiers(raw) {
      if (!raw) return [];
      try {
        const tiers = JSON.parse(raw);
        if (!Array.isArray(tiers)) return [];
        return tiers
          .map(function (tier) {
            return {
              min: parseInt(tier.min, 10) || 0,
              percent: parseFloat(tier.percent) || 0,
            };
          })
          .filter(function (tier) {
            return tier.min > 0 && tier.percent > 0;
          })
          .sort(function (a, b) {
            return b.min - a.min;
          });
      } catch (e) {
        return [];
      }
    }

    getVolumeDiscountPercent(pieces, unitPrice) {
      if (!isFinite(unitPrice) || unitPrice <= 0) return 0;
      const subtotal = unitPrice * pieces;
      if (this.minOrderAmount > 0 && subtotal < this.minOrderAmount) {
        return 0;
      }
      for (let i = 0; i < this.volumeDiscountTiers.length; i++) {
        const tier = this.volumeDiscountTiers[i];
        if (pieces >= tier.min) return tier.percent;
      }
      return 0;
    }

    discountsEligibleForSubtotal(subtotal) {
      if (this.minOrderAmount <= 0) return true;
      return subtotal >= this.minOrderAmount;
    }

    getDisplayTiersAscending() {
      return this.volumeDiscountTiers
        .slice()
        .sort(function (a, b) {
          return a.min - b.min;
        });
    }

    updateQuantityDiscountUI(pricing) {
      if (!this.qtyDiscountsPanel || !this.qtyDiscountListEl) return;

      if (this.volumeDiscountTiers.length === 0) {
        this.qtyDiscountsPanel.hidden = true;
        return;
      }

      this.qtyDiscountsPanel.hidden = false;

      if (!pricing) {
        if (this.qtyDiscountGateEl) {
          this.qtyDiscountGateEl.hidden = false;
          this.qtyDiscountGateEl.textContent =
            'Generate a preview and set your size to see quantity discount prices.';
        }
        this.renderQuantityDiscountRows(null);
        return;
      }

      const unitPrice = pricing.unitPrice;
      const currentPieces = pricing.pieces;
      const discountsUnlocked = this.discountsEligibleForSubtotal(pricing.subtotal);

      if (this.qtyDiscountGateEl) {
        if (this.minOrderAmount > 0 && !discountsUnlocked) {
          this.qtyDiscountGateEl.hidden = false;
          this.qtyDiscountGateEl.textContent =
            'Quantity discounts apply when order subtotal reaches ' +
            this.formatMoney(this.minOrderAmount) +
            ' (before discount).';
        } else {
          this.qtyDiscountGateEl.hidden = true;
          this.qtyDiscountGateEl.textContent = '';
        }
      }

      this.renderQuantityDiscountRows(pricing);
    }

    renderQuantityDiscountRows(pricing) {
      if (!this.qtyDiscountListEl) return;

      const unitPrice = pricing ? pricing.unitPrice : null;
      const currentPieces = pricing ? pricing.pieces : 0;
      const currentSubtotal = pricing ? pricing.subtotal : 0;
      const currentDiscountsUnlocked = pricing
        ? this.discountsEligibleForSubtotal(currentSubtotal)
        : false;
      const tiers = this.getDisplayTiersAscending();
      let activeTierMin = 0;
      if (currentDiscountsUnlocked) {
        tiers.forEach(function (tier) {
          if (currentPieces >= tier.min && tier.min > activeTierMin) {
            activeTierMin = tier.min;
          }
        });
      }
      const self = this;
      this.qtyDiscountListEl.innerHTML = '';

      tiers.forEach(function (tier) {
        const tierSubtotal = unitPrice ? unitPrice * tier.min : null;
        const tierEligible =
          tierSubtotal != null && self.discountsEligibleForSubtotal(tierSubtotal);
        const tierPercent = tierEligible ? tier.percent : 0;
        const saleTotal =
          tierSubtotal != null ? tierSubtotal * (1 - tierPercent / 100) : null;
        const isActive = tierEligible && tier.min === activeTierMin;

        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'embroidery-generator__qty-discount-row';
        if (isActive) row.classList.add('is-active');
        if (!tierEligible && unitPrice != null) row.classList.add('is-locked');
        if (!unitPrice) row.classList.add('is-pending');
        row.setAttribute('role', 'radio');
        row.setAttribute('aria-checked', isActive ? 'true' : 'false');
        row.dataset.tierQty = String(tier.min);

        row.innerHTML =
          '<span class="embroidery-generator__qty-discount-radio" aria-hidden="true"></span>' +
          '<span class="embroidery-generator__qty-discount-buy">Buy: <strong>' +
          tier.min +
          '+</strong></span>' +
          '<span class="embroidery-generator__qty-discount-prices">' +
          '<span class="embroidery-generator__qty-discount-sale">' +
          (saleTotal != null ? self.formatMoney(saleTotal) : '—') +
          '</span>' +
          (tierPercent > 0 && tierSubtotal != null
            ? '<span class="embroidery-generator__qty-discount-compare">' +
              self.formatMoney(tierSubtotal) +
              '</span>'
            : '') +
          '</span>' +
          (tierPercent > 0
            ? '<span class="embroidery-generator__qty-discount-save">Save: ' + tierPercent + '%</span>'
            : '<span class="embroidery-generator__qty-discount-save embroidery-generator__qty-discount-save--muted">—</span>');

        row.addEventListener('click', function () {
          if (!self.pieceQtyInput || !pricing) return;
          const nextQty = Math.max(self.minQty, tier.min);
          self.pieceQtyInput.value = String(nextQty);
          self.finalizeSizeUpdate();
        });

        self.qtyDiscountListEl.appendChild(row);
      });
    }

    meetsMinimumOrder(pricing) {
      if (!pricing) return false;
      if (pricing.pieces < this.minQty) return false;
      if (this.minOrderAmount > 0 && pricing.subtotal < this.minOrderAmount) return false;
      return true;
    }

    getCartBlockReason() {
      const pricing = this.getPricing();
      if (!pricing) return '';
      if (pricing.pieces < this.minQty) {
        return 'Minimum quantity is ' + this.minQty + ' pcs.';
      }
      if (this.minOrderAmount > 0 && pricing.subtotal < this.minOrderAmount) {
        return (
          'Minimum order is ' +
          this.formatMoney(this.minOrderAmount) +
          ' (before discount). Increase quantity or size.'
        );
      }
      return '';
    }

    init() {
      if (!this.lambdaUrl) {
        this.setStatus('error', 'Embroidery service is not configured.');
        return;
      }

      if (!this.widthInput || !this.heightInput) {
        this.setStatus('error', 'Size fields are missing. Please re-publish the embroidery snippet.');
        return;
      }

      this.widthInput.min = String(this.minW);
      this.widthInput.max = String(this.maxW);
      this.heightInput.min = String(this.minH);
      this.heightInput.max = String(this.maxH);

      this.fileInput.addEventListener('change', this.onFileChange.bind(this));
      this.generateBtn.addEventListener('click', this.onGenerate.bind(this));
      if (this.cropBtn) {
        this.cropBtn.addEventListener('click', () => this.openCrop());
      }
      if (this.progressCancelBtn) {
        this.progressCancelBtn.addEventListener('click', this.onCancelGenerate.bind(this));
      }

      this.widthInput.addEventListener('input', (e) => this.onSizeFieldEdit('width', e));
      this.heightInput.addEventListener('input', (e) => this.onSizeFieldEdit('height', e));
      this.widthInput.addEventListener('change', (e) => this.onSizeFieldEdit('width', e));
      this.heightInput.addEventListener('change', (e) => this.onSizeFieldEdit('height', e));

      const widthDec = this.root.querySelector('[data-embroidery-width-dec]');
      const widthInc = this.root.querySelector('[data-embroidery-width-inc]');
      const heightDec = this.root.querySelector('[data-embroidery-height-dec]');
      const heightInc = this.root.querySelector('[data-embroidery-height-inc]');
      if (widthDec) widthDec.addEventListener('click', () => this.nudgeSize('width', -SIZE_STEP));
      if (widthInc) widthInc.addEventListener('click', () => this.nudgeSize('width', SIZE_STEP));
      if (heightDec) heightDec.addEventListener('click', () => this.nudgeSize('height', -SIZE_STEP));
      if (heightInc) heightInc.addEventListener('click', () => this.nudgeSize('height', SIZE_STEP));

      this.originalImg.addEventListener('load', () => {
        this.readDimensionsFromUploadedImage(this.originalImg);
      });
      // Never overwrite aspect from generated Cutout image (it is padded to 1:1).
      this.generatedImg.addEventListener('load', () => {
        if (this.hasGenerated && this.sizePanel && !this.sizePanel.hidden) {
          if (!this.aspectRatio) {
            this.readDimensionsFromUploadedImage(this.originalImg);
          }
          this.applyDefaultProportionalSize();
          this.finalizeSizeUpdate();
        }
      });

      this.setupQuantityBridge();
      this.lockCart(true);
      this.setGenerateEnabled(false);
      this.updatePriceUI();
      this.updateImageDimsUI();
      this.hideUploadLiftWidgets();
      this.hideStorefrontPrice();
      this.protectPreviewImages();
      this.updateProductPriceDisplay(null);
      this.updateQuantityDiscountUI(null);
      this.refreshPreviewQuotaStatus();
      this.syncQuotaFromServer();
    }

    async syncQuotaFromServer() {
      if (!this.lambdaUrl || !this.dailyLimitEnabled) return;
      try {
        const identity = this.getQuotaIdentityPayload();
        const data = await this.postLambda(
          {
            action: 'check-generate-limit',
            clientId: identity.clientId,
            customerId: identity.customerId,
            dailyLimitEnabled: identity.dailyLimitEnabled,
            dailyLimit: identity.dailyLimit,
          },
          15000
        );
        if (data && data.quota) {
          this.applyQuotaFromServer(data.quota);
          this.refreshPreviewQuotaStatus();
          if (this.selectedFile) {
            this.setGenerateEnabled(this.canGeneratePreview() && !this.isGenerating);
          }
        }
      } catch (e) {
        // Older Lambda without check-generate-limit — keep local soft limit
      }
    }

    getDayKey() {
      try {
        return new Intl.DateTimeFormat('en-CA', {
          timeZone: QUOTA_TZ,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(new Date());
      } catch (e) {
        const now = new Date();
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, '0');
        const d = String(now.getDate()).padStart(2, '0');
        return y + '-' + m + '-' + d;
      }
    }

    getCustomerKey() {
      if (this.customerId) {
        return 'c' + this.customerId;
      }
      try {
        let guestId = window.localStorage.getItem(GUEST_ID_KEY);
        if (!guestId) {
          guestId =
            'g' +
            Date.now().toString(36) +
            Math.random().toString(36).slice(2, 10);
          window.localStorage.setItem(GUEST_ID_KEY, guestId);
        }
        return guestId;
      } catch (e) {
        return 'g_anon';
      }
    }

    getQuotaIdentityPayload() {
      return {
        clientId: this.getCustomerKey(),
        customerId: this.customerId || '',
        dailyLimitEnabled: this.dailyLimitEnabled !== false,
        dailyLimit: this.maxPreviewsPerDay,
      };
    }

    getPreviewStorageKey() {
      return PREVIEW_STORAGE_PREFIX + this.getDayKey() + '_' + this.getCustomerKey();
    }

    getPreviewCount() {
      if (!this.dailyLimitEnabled) return 0;
      try {
        const raw = window.localStorage.getItem(this.getPreviewStorageKey());
        const n = parseInt(raw || '0', 10);
        return isFinite(n) && n > 0 ? n : 0;
      } catch (e) {
        return 0;
      }
    }

    setPreviewCount(count) {
      if (!this.dailyLimitEnabled) return 0;
      const n = Math.max(0, parseInt(count, 10) || 0);
      try {
        window.localStorage.setItem(this.getPreviewStorageKey(), String(n));
      } catch (e) {
        // ignore quota persistence errors
      }
      return n;
    }

    getRemainingPreviews() {
      if (!this.dailyLimitEnabled) return 999;
      return Math.max(0, this.maxPreviewsPerDay - this.getPreviewCount());
    }

    canGeneratePreview() {
      if (!this.dailyLimitEnabled) return true;
      return this.getRemainingPreviews() > 0;
    }

    consumePreviewQuota() {
      if (!this.dailyLimitEnabled) return 0;
      return this.setPreviewCount(this.getPreviewCount() + 1);
    }

    applyQuotaFromServer(quota) {
      if (!this.dailyLimitEnabled || !quota || typeof quota !== 'object') return;
      if (quota.enabled === false) {
        this.dailyLimitEnabled = false;
        return;
      }
      if (quota.used != null) {
        this.setPreviewCount(quota.used);
      } else if (quota.remaining != null && quota.limit != null) {
        this.setPreviewCount(Math.max(0, Number(quota.limit) - Number(quota.remaining)));
      }
      if (quota.limit != null) {
        const limit = parseInt(quota.limit, 10);
        if (isFinite(limit) && limit > 0) this.maxPreviewsPerDay = limit;
      }
    }

    dailyLimitMessage() {
      return (
        'Daily preview limit reached (' +
        this.maxPreviewsPerDay +
        ' per day). Please try again tomorrow.'
      );
    }

    refreshPreviewQuotaStatus() {
      if (!this.dailyLimitEnabled) {
        if (!this.selectedFile && !this.hasGenerated) {
          this.setStatus('', 'Upload an image to generate an embroidery preview.');
        }
        return;
      }
      const remaining = this.getRemainingPreviews();
      if (!this.canGeneratePreview()) {
        this.setGenerateEnabled(false);
        this.setStatus('error', this.dailyLimitMessage());
        return;
      }
      if (!this.selectedFile && !this.hasGenerated) {
        this.setStatus(
          '',
          'Upload an image to generate an embroidery preview. (' +
            remaining +
            ' preview' +
            (remaining === 1 ? '' : 's') +
            ' left today)'
        );
      }
    }

    /**
     * Background removal DISABLED (client request).
     * Always show the exact image returned by Cutout.Pro API.
     */
    async prepareTransparentPreview(imageUrl) {
      return { previewUrl: imageUrl, fulfillmentUrl: '' };
    }

    async isolatePatchInWorker() {
      return null;
    }

    async isolatePatchOnTransparent() {
      return null;
    }

    async removeEdgeConnectedFabricAsync() {
      return;
    }

    runFabricCleanupWorker() {
      return Promise.resolve(null);
    }

    findContentBoundsFromData() {
      return null;
    }

    async uploadFulfillmentPng(dataUrl) {
      if (!dataUrl || typeof dataUrl !== 'string') return '';
      if (dataUrl.indexOf('data:') === 0) return '';
      try {
        const response = await fetch(dataUrl);
        const blob = await response.blob();
        const file = new File([blob], 'embroidery-fulfillment.png', { type: 'image/png' });
        const upload = await this.uploadImageFile(file);
        return this.buildCartThumbnailUrl(upload.key) || upload.publicUrl || '';
      } catch (e) {
        console.warn('[embroidery-generator] fulfillment upload skipped', e);
        return '';
      }
    }

    updateFulfillmentDownloadLink() {
      // Download button removed per client request — fulfillment links stay in order properties only.
    }

    safePropertyUrl(url) {
      if (!url || typeof url !== 'string') return '';
      const trimmed = url.trim();
      // Never store base64/data URLs on the order — they make admin unusable
      if (trimmed.indexOf('data:') === 0) return '';
      if (trimmed.length > 2000) return '';
      return trimmed;
    }

    // Back-compat alias
    removeNearWhiteBackground(sourceUrl) {
      return this.isolatePatchOnTransparent(sourceUrl).then(function (result) {
        return result && result.dataUrl ? result.dataUrl : null;
      });
    }

    setupQuantityBridge() {
      const form = this.getProductForm();
      if (!form) return;

      this.pieceQtyInput =
        form.querySelector('input[name="quantity"]') ||
        document.querySelector('#Quantity-' + this.sectionId) ||
        document.querySelector('quantity-input input[name="quantity"]') ||
        document.querySelector('quantity-input input[name="embroidery_piece_qty"]');

      if (this.pieceQtyInput) {
        this.cartQtyInput = document.createElement('input');
        this.cartQtyInput.type = 'hidden';
        this.cartQtyInput.name = 'quantity';
        this.cartQtyInput.value = '1';
        form.appendChild(this.cartQtyInput);

        this.pieceQtyInput.name = 'embroidery_piece_qty';
        this.pieceQtyInput.min = String(this.minQty);
        this.pieceQtyInput.setAttribute('data-min', String(this.minQty));
        if (this.pieceQtyInput.dataset) this.pieceQtyInput.dataset.min = String(this.minQty);

        const current = parseInt(this.pieceQtyInput.value, 10);
        this.pieceQtyInput.value = String(
          isFinite(current) && current >= this.minQty ? current : this.minQty
        );

        this.pieceQtyInput.addEventListener('input', () => this.enforceMinQty());
        this.pieceQtyInput.addEventListener('change', () => {
          this.enforceMinQty();
          this.finalizeSizeUpdate();
        });
      }

      form.addEventListener(
        'submit',
        () => {
          this.enforceMinQty();
          this.syncCartQuantity();
          this.writeFormProperties();
        },
        true
      );
    }

    enforceMinQty() {
      if (!this.pieceQtyInput) return this.minQty;
      let q = parseInt(this.pieceQtyInput.value, 10);
      if (!isFinite(q) || q < this.minQty) {
        q = this.minQty;
        this.pieceQtyInput.value = String(this.minQty);
      }
      return q;
    }

    getProductForm() {
      if (this.productFormId) {
        const byId = document.getElementById(this.productFormId);
        if (byId) return byId;
      }
      return (
        document.querySelector('form[data-type="add-to-cart-form"]') ||
        document.querySelector('product-form form')
      );
    }

    hideStorefrontPrice() {
      const priceRoot = document.querySelector('#price-' + this.sectionId);
      if (priceRoot) {
        priceRoot.hidden = true;
        priceRoot.style.setProperty('display', 'none', 'important');
      }

      const volumeNote = document.querySelector('#Volume-Note-' + this.sectionId);
      if (volumeNote) {
        volumeNote.style.setProperty('display', 'none', 'important');
      }
    }

    protectPreviewImages() {
      const blockSave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        return false;
      };

      const previews = this.root.querySelector('.embroidery-generator__previews');
      if (previews) {
        previews.addEventListener('contextmenu', blockSave);
        previews.addEventListener('dragstart', blockSave);
      }

      [this.originalImg, this.generatedImg].forEach((img) => {
        if (!img) return;
        img.setAttribute('draggable', 'false');
        img.addEventListener('contextmenu', blockSave);
        img.addEventListener('dragstart', blockSave);
      });
    }

    hideUploadLiftWidgets() {
      const selectors = [
        '[class*="cloudlift"]',
        '[id*="cloudlift"]',
        '[class*="Cloudlift"]',
        '[id*="Cloudlift"]',
        '.cl-upload',
        '.cl-option',
        '[data-cloudlift]',
        '.live-product-options',
        '[class*="live-product-option"]',
      ];
      document.querySelectorAll(selectors.join(',')).forEach((el) => {
        el.style.setProperty('display', 'none', 'important');
      });
    }

    lockCart(locked) {
      const form = this.getProductForm();
      if (!form) return;

      const submit = form.querySelector('[type="submit"][name="add"], .product-form__submit');
      if (submit) {
        if (locked) {
          submit.setAttribute('disabled', 'disabled');
          submit.setAttribute('aria-disabled', 'true');
          submit.dataset.embroideryLocked = 'true';
        } else if (submit.dataset.embroideryLocked === 'true') {
          delete submit.dataset.embroideryLocked;
          if (this.variantAvailable) {
            submit.removeAttribute('disabled');
            submit.removeAttribute('aria-disabled');
          } else {
            submit.setAttribute('disabled', 'disabled');
          }
        }
      }

      form.querySelectorAll('.shopify-payment-button').forEach((el) => {
        if (locked) {
          el.style.pointerEvents = 'none';
          el.style.opacity = '0.45';
          el.setAttribute('aria-hidden', 'true');
        } else {
          el.style.pointerEvents = '';
          el.style.opacity = '';
          el.removeAttribute('aria-hidden');
        }
      });
    }

    setGenerateEnabled(enabled) {
      this.generateBtn.disabled = !enabled || this.isGenerating;
    }

    setCropEnabled(enabled) {
      if (!this.cropBtn) return;
      this.cropBtn.disabled = !enabled || this.isGenerating;
    }

    setStatus(state, message) {
      this.statusEl.dataset.state = state || '';
      if (state === 'loading') {
        this.statusEl.innerHTML =
          '<span class="embroidery-generator__spinner" aria-hidden="true"></span>' +
          this.escapeHtml(message);
      } else {
        this.statusEl.textContent = message || '';
      }
    }

    setProgressPercent(value) {
      const pct = Math.max(0, Math.min(100, Math.round(value)));
      this.progressValue = pct;
      if (this.progressFillEl) this.progressFillEl.style.width = pct + '%';
      if (this.progressPctEl) this.progressPctEl.textContent = pct + '%';
    }

    showProgress(label) {
      if (!this.progressEl) return;
      if (this.progressLabelEl && label) this.progressLabelEl.textContent = label;
      this.progressEl.hidden = false;
      this.progressEl.setAttribute('aria-busy', 'true');
      this.progressStartedAt = Date.now();
      this.updateElapsedLabel();
      if (this.elapsedTimer) clearInterval(this.elapsedTimer);
      this.elapsedTimer = setInterval(() => {
        this.updateElapsedLabel();
        // Keep the UI feeling alive after the bar reaches ~90%
        if (this.progressValue >= 90 && this.progressValue < 95) {
          this.setProgressPercent(this.progressValue + 0.15);
        }
        if (this.progressLabelEl && this.progressValue >= 88) {
          const secs = Math.floor((Date.now() - this.progressStartedAt) / 1000);
          if (secs >= 45) {
            this.progressLabelEl.textContent = 'Still generating… please wait';
          }
        }
      }, 1000);
      this.setProgressPercent(this.progressValue || 2);
      try {
        this.progressEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) {
        // ignore
      }
    }

    updateElapsedLabel() {
      if (!this.progressElapsedEl || !this.progressStartedAt) return;
      const secs = Math.max(0, Math.floor((Date.now() - this.progressStartedAt) / 1000));
      this.progressElapsedEl.textContent = secs + 's elapsed';
    }

    hideProgress() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer);
        this.progressTimer = null;
      }
      if (this.elapsedTimer) {
        clearInterval(this.elapsedTimer);
        this.elapsedTimer = null;
      }
      if (this.progressEl) {
        this.progressEl.hidden = true;
        this.progressEl.setAttribute('aria-busy', 'false');
      }
      this.progressValue = 0;
      this.progressStartedAt = 0;
      if (this.progressFillEl) this.progressFillEl.style.width = '0%';
      if (this.progressPctEl) this.progressPctEl.textContent = '0%';
      if (this.progressElapsedEl) this.progressElapsedEl.textContent = '0s elapsed';
    }

    onCancelGenerate(event) {
      if (event) event.preventDefault();
      if (!this.isGenerating) return;
      this.userCancelled = true;
      if (this.activeAbortController) {
        try {
          this.activeAbortController.abort();
        } catch (e) {
          // ignore
        }
      }
      this.hideProgress();
      this.isGenerating = false;
      this.setGenerateEnabled(Boolean(this.selectedFile) && this.canGeneratePreview());
      this.setCropEnabled(Boolean(this.selectedFile));
      this.setStatus('error', 'Generation cancelled. You can try again.');
      this.refreshCartLock();
    }

    startProgressAnimation(targetPct, durationMs) {
      if (this.progressTimer) {
        clearInterval(this.progressTimer);
        this.progressTimer = null;
      }
      const start = this.progressValue || 0;
      const end = Math.min(95, targetPct);
      const startedAt = Date.now();
      const duration = Math.max(800, durationMs || 40000);

      this.progressTimer = setInterval(() => {
        const elapsed = Date.now() - startedAt;
        const t = Math.min(1, elapsed / duration);
        // Ease-out so it feels active early, then slows near the end
        const eased = 1 - Math.pow(1 - t, 2.2);
        this.setProgressPercent(start + (end - start) * eased);
        if (t >= 1) {
          clearInterval(this.progressTimer);
          this.progressTimer = null;
        }
      }, 120);
    }

    async finishProgress() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer);
        this.progressTimer = null;
      }
      this.setProgressPercent(100);
      if (this.progressLabelEl) this.progressLabelEl.textContent = 'Almost done…';
      await new Promise(function (resolve) {
        setTimeout(resolve, 350);
      });
      this.hideProgress();
    }

    /**
     * Downscale large phone photos before upload — faster upload + Cutout.
     */
    async prepareUploadFile(file) {
      if (!file || !file.type || !file.type.startsWith('image/')) return file;

      try {
        const objectUrl = URL.createObjectURL(file);
        const img = await Promise.race([
          new Promise(function (resolve, reject) {
            const image = new Image();
            image.onload = function () {
              resolve(image);
            };
            image.onerror = reject;
            image.src = objectUrl;
          }),
          new Promise(function (_, reject) {
            setTimeout(function () {
              reject(new Error('Image load timeout'));
            }, 10000);
          }),
        ]);
        URL.revokeObjectURL(objectUrl);

        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        if (!w || !h) return file;
        const longest = Math.max(w, h);
        if (longest <= UPLOAD_MAX_EDGE) return file;

        const scale = UPLOAD_MAX_EDGE / longest;
        const tw = Math.max(1, Math.round(w * scale));
        const th = Math.max(1, Math.round(h * scale));
        const canvas = document.createElement('canvas');
        canvas.width = tw;
        canvas.height = th;
        const ctx = canvas.getContext('2d');
        if (!ctx) return file;
        ctx.drawImage(img, 0, 0, tw, th);

        const blob = await Promise.race([
          new Promise(function (resolve) {
            canvas.toBlob(resolve, 'image/jpeg', 0.88);
          }),
          new Promise(function (resolve) {
            setTimeout(function () {
              resolve(null);
            }, 8000);
          }),
        ]);
        if (!blob) return file;

        const baseName = (file.name || 'upload').replace(/\.[^.]+$/, '') || 'upload';
        return new File([blob], baseName + '.jpg', { type: 'image/jpeg' });
      } catch (e) {
        console.warn('[embroidery-generator] image resize skipped', e);
        return file;
      }
    }

    escapeHtml(text) {
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    formatMoney(amount) {
      const n = Number(amount);
      if (!isFinite(n)) return '—';
      return '$' + n.toFixed(2) + ' USD';
    }

    roundToStep(value) {
      return Math.round(value / SIZE_STEP) * SIZE_STEP;
    }

    clamp(value, min, max) {
      return Math.min(max, Math.max(min, value));
    }

    /**
     * Read width/height from the customer's uploaded image only.
     * Prefer content bounding-box (skip transparent / near-white padding)
     * so proportion matches the real artwork, not empty canvas.
     */
    readDimensionsFromUploadedImage(img) {
      if (!img || !img.naturalWidth || !img.naturalHeight) return false;

      this.imagePixelWidth = img.naturalWidth;
      this.imagePixelHeight = img.naturalHeight;
      this.aspectRatio = img.naturalWidth / img.naturalHeight;

      // Use natural ratio only — full-res canvas scans freeze/unclickable on large uploads
      this.updateImageDimsUI();
      return true;
    }

    findContentBounds(ctx, width, height) {
      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;
      let minX = width;
      let minY = height;
      let maxX = -1;
      let maxY = -1;

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const i = (y * width + x) * 4;
          const a = data[i + 3];
          if (a < 12) continue;
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          // Skip near-white plate / empty background
          if (r >= WHITE_BG_THRESHOLD && g >= WHITE_BG_THRESHOLD && b >= WHITE_BG_THRESHOLD) {
            continue;
          }
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }

      if (maxX < minX || maxY < minY) return null;
      return {
        x: minX,
        y: minY,
        width: maxX - minX + 1,
        height: maxY - minY + 1,
      };
    }

    async applySizeFromArtwork(imageUrl) {
      const image = await this.loadArtworkImage(imageUrl);
      if (!image) {
        this.sizeUsesArtwork = false;
        this.readDimensionsFromUploadedImage(this.originalImg);
        this.applyDefaultProportionalSize();
        return;
      }

      this.artworkImage = image;
      const fullW = image.naturalWidth || image.width;
      const fullH = image.naturalHeight || image.height;
      const bounds = this.measureArtworkBounds(image);
      const useBounds = Boolean(bounds && this.artworkBoundsDiffer(bounds, fullW, fullH));
      const box = useBounds ? bounds : { x: 0, y: 0, width: fullW, height: fullH };

      this.sizeUsesArtwork = useBounds;
      this.aspectRatio = box.width / box.height;
      this.imagePixelWidth = box.width;
      this.imagePixelHeight = box.height;
      this.updateImageDimsUI();

      if (useBounds) {
        const trimmed = this.cropImageToCanvas(image, box);
        const blob = await this.canvasToBlob(trimmed, 'image/png');
        if (blob) {
          const objectUrl = URL.createObjectURL(blob);
          this.generatedImg.src = objectUrl;
          this.artworkImage = (await this.tryLoadImage(objectUrl, false)) || image;
        }
        try {
          const uploaded = await this.uploadCanvasPng(trimmed);
          if (uploaded) this.trimmedFulfillmentUrl = uploaded;
        } catch (e) {
          console.warn('[embroidery-generator] trimmed print file upload skipped', e);
        }
      }

      this.applyDefaultProportionalSize();
    }

    async loadArtworkImage(imageUrl) {
      const candidates = [];
      const proxied = this.buildCartThumbnailUrl(this.generatedKey);
      if (proxied) candidates.push(proxied);
      if (imageUrl) candidates.push(imageUrl);

      for (let i = 0; i < candidates.length; i++) {
        const img = await this.tryLoadImage(candidates[i], true);
        if (img && this.canReadImage(img)) return img;
      }

      if (!imageUrl || String(imageUrl).indexOf('data:') === 0) return null;
      try {
        const data = await this.postLambda(
          { action: 'get-preview-image', imageUrl: imageUrl },
          45000
        );
        const dataUrl = (data && (data.dataUrl || (data.data && data.data.dataUrl))) || '';
        if (!dataUrl) return null;
        const img = await this.tryLoadImage(dataUrl, false);
        if (img && this.canReadImage(img)) return img;
      } catch (e) {
        console.warn('[embroidery-generator] artwork image proxy skipped', e);
      }
      return null;
    }

    tryLoadImage(url, crossOrigin) {
      return new Promise(function (resolve) {
        const image = new Image();
        if (crossOrigin) image.crossOrigin = 'anonymous';
        image.onload = function () {
          resolve(image);
        };
        image.onerror = function () {
          resolve(null);
        };
        image.src = url;
      });
    }

    canReadImage(img) {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, 1, 1);
        ctx.getImageData(0, 0, 1, 1);
        return true;
      } catch (e) {
        return false;
      }
    }

    measureArtworkBounds(img) {
      const fullW = img.naturalWidth || img.width;
      const fullH = img.naturalHeight || img.height;
      if (!fullW || !fullH) return null;
      const scale = Math.min(1, 1000 / Math.max(fullW, fullH));
      const w = Math.max(1, Math.round(fullW * scale));
      const h = Math.max(1, Math.round(fullH * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0, w, h);
      let pixels;
      try {
        pixels = ctx.getImageData(0, 0, w, h);
      } catch (e) {
        return null;
      }
      const bounds = this.findArtworkBounds(pixels);
      if (!bounds) return null;
      const inv = 1 / scale;
      const x = Math.max(0, Math.floor(bounds.x * inv));
      const y = Math.max(0, Math.floor(bounds.y * inv));
      const right = Math.min(fullW, Math.ceil((bounds.x + bounds.width) * inv));
      const bottom = Math.min(fullH, Math.ceil((bounds.y + bounds.height) * inv));
      const width = Math.max(1, right - x);
      const height = Math.max(1, bottom - y);
      if (width * height < fullW * fullH * 0.08) return null;
      return { x: x, y: y, width: width, height: height };
    }

    artworkBoundsDiffer(bounds, fullW, fullH) {
      if (!bounds || !fullW || !fullH) return false;
      return bounds.width < fullW * 0.97 || bounds.height < fullH * 0.97;
    }

    findArtworkBounds(imageData) {
      const width = imageData.width;
      const height = imageData.height;
      const data = imageData.data;
      const isPadding = function (x, y) {
        const i = (y * width + x) * 4;
        const alpha = data[i + 3];
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        if (alpha < 16) return true;
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        if (max < 28 && max - min < 18) return true;
        if (min >= 245) return true;
        return false;
      };
      const rowIsPadding = function (y) {
        let padding = 0;
        for (let x = 0; x < width; x++) if (isPadding(x, y)) padding++;
        return padding / width >= 0.985;
      };
      const colIsPadding = function (x) {
        let padding = 0;
        for (let y = 0; y < height; y++) if (isPadding(x, y)) padding++;
        return padding / height >= 0.985;
      };
      let top = 0;
      while (top < height - 1 && rowIsPadding(top)) top++;
      let bottom = height - 1;
      while (bottom > top && rowIsPadding(bottom)) bottom--;
      let left = 0;
      while (left < width - 1 && colIsPadding(left)) left++;
      let right = width - 1;
      while (right > left && colIsPadding(right)) right--;
      if (right <= left || bottom <= top) return null;
      return {
        x: left,
        y: top,
        width: right - left + 1,
        height: bottom - top + 1,
      };
    }

    cropImageToCanvas(img, box) {
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(box.width));
      canvas.height = Math.max(1, Math.round(box.height));
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        img,
        Math.round(box.x),
        Math.round(box.y),
        canvas.width,
        canvas.height,
        0,
        0,
        canvas.width,
        canvas.height
      );
      return canvas;
    }

    canvasToBlob(canvas, type) {
      return new Promise(function (resolve) {
        canvas.toBlob(function (blob) {
          resolve(blob || null);
        }, type || 'image/png');
      });
    }

    async uploadCanvasPng(canvas) {
      let source = canvas;
      const longest = Math.max(canvas.width, canvas.height);
      if (longest > 2000) source = this.scaleCanvas(canvas, 2000 / longest);
      let blob = await this.canvasToBlob(source, 'image/png');
      if (blob && blob.size > 4200000) {
        const next = Math.max(source.width, source.height);
        source = this.scaleCanvas(source, 1400 / next);
        blob = await this.canvasToBlob(source, 'image/png');
      }
      if (!blob || blob.size > 4500000) return '';
      const file = new File([blob], 'embroidery-print.png', { type: 'image/png' });
      const upload = await this.uploadImageFile(file);
      return this.buildCartThumbnailUrl(upload.key) || upload.publicUrl || '';
    }

    scaleCanvas(canvas, scale) {
      const next = document.createElement('canvas');
      next.width = Math.max(1, Math.round(canvas.width * scale));
      next.height = Math.max(1, Math.round(canvas.height * scale));
      next.getContext('2d').drawImage(canvas, 0, 0, next.width, next.height);
      return next;
    }

    async openCrop() {
      if (this.isGenerating) return;
      if (this.hasGenerated && this.artworkImage) {
        this.cropTarget = 'generated';
        this.cropSourceImage = this.artworkImage;
      } else if (this.selectedFile) {
        this.cropTarget = 'upload';
        this.cropSourceImage = await this.tryLoadImage(URL.createObjectURL(this.selectedFile), false);
      }
      if (!this.cropSourceImage) {
        this.setStatus('error', 'Upload an image before cropping.');
        return;
      }
      this.ensureCropModal();
      const img = this.cropModal.querySelector('img');
      img.onload = () => {
        this.cropRect = { x: 0, y: 0, w: img.clientWidth, h: img.clientHeight };
        this.paintCropBox();
      };
      img.src = this.cropSourceImage.src;
      this.cropModal.classList.add('is-open');
    }

    ensureCropModal() {
      if (this.cropModal) return;
      const modal = document.createElement('div');
      modal.className = 'embroidery-crop-modal';
      modal.innerHTML =
        '<div class="embroidery-crop-modal__card">' +
          '<div class="embroidery-crop-modal__head"><h3>Crop image</h3><button type="button" data-crop-close aria-label="Close">×</button></div>' +
          '<p>Drag the box around the artwork you want to print. Empty background is left out, and the design is not stretched.</p>' +
          '<div class="embroidery-crop-stage"><img alt="Crop preview"><div class="embroidery-crop-box" data-crop-move>' +
            '<span data-handle="nw"></span><span data-handle="ne"></span><span data-handle="sw"></span><span data-handle="se"></span>' +
          '</div></div>' +
          '<div class="embroidery-crop-modal__actions">' +
            '<button type="button" class="button button--secondary" data-crop-close>Cancel</button>' +
            '<button type="button" class="button button--primary" data-crop-apply>Apply crop</button>' +
          '</div>' +
        '</div>';
      document.body.appendChild(modal);
      modal.addEventListener('click', (event) => {
        if (event.target === modal || event.target.closest('[data-crop-close]')) {
          modal.classList.remove('is-open');
        }
      });
      modal.querySelector('[data-crop-apply]').addEventListener('click', () => this.applyCrop());
      modal.querySelector('.embroidery-crop-box').addEventListener('pointerdown', (event) => {
        this.startCropDrag(event);
      });
      this.cropModal = modal;
    }

    paintCropBox() {
      const box = this.cropModal.querySelector('.embroidery-crop-box');
      const rect = this.cropRect;
      box.style.left = rect.x + 'px';
      box.style.top = rect.y + 'px';
      box.style.width = rect.w + 'px';
      box.style.height = rect.h + 'px';
    }

    startCropDrag(event) {
      event.preventDefault();
      const handle = event.target.dataset.handle || 'move';
      const start = { x: event.clientX, y: event.clientY, box: Object.assign({}, this.cropRect) };
      const img = this.cropModal.querySelector('img');
      const move = (ev) => {
        const dx = ev.clientX - start.x;
        const dy = ev.clientY - start.y;
        let x = start.box.x;
        let y = start.box.y;
        let w = start.box.w;
        let h = start.box.h;
        const maxW = img.clientWidth;
        const maxH = img.clientHeight;
        if (handle === 'move') {
          x = Math.min(Math.max(0, x + dx), Math.max(0, maxW - w));
          y = Math.min(Math.max(0, y + dy), Math.max(0, maxH - h));
        } else {
          if (handle.indexOf('e') !== -1) w = w + dx;
          if (handle.indexOf('s') !== -1) h = h + dy;
          if (handle.indexOf('w') !== -1) {
            x = x + dx;
            w = w - dx;
          }
          if (handle.indexOf('n') !== -1) {
            y = y + dy;
            h = h - dy;
          }
          w = Math.max(24, w);
          h = Math.max(24, h);
          x = Math.min(Math.max(0, x), Math.max(0, maxW - 24));
          y = Math.min(Math.max(0, y), Math.max(0, maxH - 24));
          w = Math.min(w, maxW - x);
          h = Math.min(h, maxH - y);
        }
        this.cropRect = { x: x, y: y, w: w, h: h };
        this.paintCropBox();
      };
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    }

    async applyCrop() {
      const source = this.cropSourceImage;
      const img = this.cropModal && this.cropModal.querySelector('img');
      if (!source || !img || !this.cropRect || !img.clientWidth || !img.clientHeight) return;
      const scaleX = source.naturalWidth / img.clientWidth;
      const scaleY = source.naturalHeight / img.clientHeight;
      const box = {
        x: Math.round(this.cropRect.x * scaleX),
        y: Math.round(this.cropRect.y * scaleY),
        width: Math.max(1, Math.round(this.cropRect.w * scaleX)),
        height: Math.max(1, Math.round(this.cropRect.h * scaleY)),
      };
      const canvas = this.cropImageToCanvas(source, box);
      this.cropModal.classList.remove('is-open');
      if (this.cropTarget === 'generated' && this.hasGenerated) {
        await this.applyGeneratedCrop(canvas);
      } else {
        await this.applyUploadCrop(canvas);
      }
    }

    async applyUploadCrop(canvas) {
      const blob = await this.canvasToBlob(canvas, 'image/png');
      if (!blob) return;
      const file = new File([blob], 'artwork-crop.png', { type: 'image/png' });
      const hadPreview = this.hasGenerated;
      this.selectedFile = file;
      this.clearGeneratedState();
      const objectUrl = URL.createObjectURL(file);
      this.originalImg.src = objectUrl;
      this.originalCard.hidden = false;
      const image = await this.tryLoadImage(objectUrl, false);
      if (image) this.readDimensionsFromUploadedImage(image);
      this.setCropEnabled(true);
      this.setGenerateEnabled(this.canGeneratePreview());
      this.setStatus(
        '',
        hadPreview
          ? 'Crop applied. Generate again so the embroidery preview and print size use this crop.'
          : 'Crop applied. Click Generate Embroidery. The print size will follow this crop.'
      );
    }

    async applyGeneratedCrop(canvas) {
      this.sizeUsesArtwork = true;
      this.aspectRatio = canvas.width / canvas.height;
      this.imagePixelWidth = canvas.width;
      this.imagePixelHeight = canvas.height;
      const blob = await this.canvasToBlob(canvas, 'image/png');
      if (blob) {
        const objectUrl = URL.createObjectURL(blob);
        this.generatedImg.src = objectUrl;
        this.artworkImage = (await this.tryLoadImage(objectUrl, false)) || this.artworkImage;
      }
      try {
        const uploaded = await this.uploadCanvasPng(canvas);
        if (uploaded) this.trimmedFulfillmentUrl = uploaded;
      } catch (e) {
        console.warn('[embroidery-generator] cropped print file upload skipped', e);
      }
      this.updateImageDimsUI();
      if (this.widthInput.value) this.setSizeFromWidth(this.widthInput.value);
      else this.applyDefaultProportionalSize();
      this.finalizeSizeUpdate();
      const sizeLabel =
        this.widthInput.value && this.heightInput.value
          ? this.widthInput.value + '" × ' + this.heightInput.value + '"'
          : 'the cropped artwork';
      this.setStatus(
        'success',
        'Crop applied. Print size is ' + sizeLabel + ' and keeps the cropped proportion. The design is not stretched.'
      );
    }

    updateImageDimsUI() {
      if (!this.imageDimsEl) return;
      if (!this.imagePixelWidth || !this.imagePixelHeight || !this.aspectRatio) {
        this.imageDimsEl.hidden = true;
        this.imageDimsEl.textContent = '';
        return;
      }
      const ratio =
        this.aspectRatio >= 1
          ? this.aspectRatio.toFixed(2) + ' : 1'
          : '1 : ' + (1 / this.aspectRatio).toFixed(2);
      this.imageDimsEl.hidden = false;
      this.imageDimsEl.textContent = this.sizeUsesArtwork
        ? 'Print size uses the artwork after the background is removed: ' +
          this.imagePixelWidth +
          ' × ' +
          this.imagePixelHeight +
          ' px. Width and height stay in proportion. The design is not stretched.'
        : 'Print size uses your image proportion: ' +
          this.imagePixelWidth +
          ' × ' +
          this.imagePixelHeight +
          ' px (ratio ' +
          ratio +
          '). Changing width updates height.';
    }

    // Kept for any older call sites
    captureAspectFromImage(img) {
      return this.readDimensionsFromUploadedImage(img);
    }

    /**
     * Fit width/height inside min/max while keeping aspect ratio (w/h).
     */
    fitSize(width, height) {
      let w = this.roundToStep(width);
      let h = this.roundToStep(height);
      const ar = this.aspectRatio;

      if (ar && isFinite(ar) && ar > 0) {
        // Prefer width-driven, then correct height
        h = this.roundToStep(w / ar);

        if (h > this.maxH) {
          h = this.maxH;
          w = this.roundToStep(h * ar);
        }
        if (w > this.maxW) {
          w = this.maxW;
          h = this.roundToStep(w / ar);
        }
        if (h < this.minH) {
          h = this.minH;
          w = this.roundToStep(h * ar);
        }
        if (w < this.minW) {
          w = this.minW;
          h = this.roundToStep(w / ar);
        }

        // Final clamp if still out of bounds due to rounding
        w = this.clamp(this.roundToStep(w), this.minW, this.maxW);
        h = this.clamp(this.roundToStep(h), this.minH, this.maxH);
      } else {
        w = this.clamp(w, this.minW, this.maxW);
        h = this.clamp(h, this.minH, this.maxH);
      }

      return { width: w, height: h };
    }

    applyDefaultProportionalSize() {
      // Largest size that fits in the product max box, keeping proportion
      let w = this.maxW;
      let h = this.aspectRatio ? w / this.aspectRatio : this.maxH;
      const fitted = this.fitSize(w, h);
      this.isSyncingProportion = true;
      this.widthInput.value = String(fitted.width);
      this.heightInput.value = String(fitted.height);
      this.isSyncingProportion = false;
    }

    setSizeFromWidth(width) {
      const w0 = this.roundToStep(parseFloat(width));
      if (!isFinite(w0)) return;
      let w = w0;
      let h = this.aspectRatio ? w / this.aspectRatio : parseFloat(this.heightInput.value) || this.minH;
      const fitted = this.fitSize(w, h);
      this.isSyncingProportion = true;
      this.widthInput.value = String(fitted.width);
      this.heightInput.value = String(fitted.height);
      this.isSyncingProportion = false;
    }

    setSizeFromHeight(height) {
      const h0 = this.roundToStep(parseFloat(height));
      if (!isFinite(h0)) return;
      let h = h0;
      let w = this.aspectRatio ? h * this.aspectRatio : parseFloat(this.widthInput.value) || this.minW;
      const fitted = this.fitSize(w, h);
      this.isSyncingProportion = true;
      this.widthInput.value = String(fitted.width);
      this.heightInput.value = String(fitted.height);
      this.isSyncingProportion = false;
    }

    nudgeSize(axis, delta) {
      if (!this.hasGenerated) return;
      if (axis === 'width') {
        const current = parseFloat(this.widthInput.value);
        const base = isFinite(current) && current > 0 ? current : this.minW;
        this.setSizeFromWidth(base + delta);
      } else {
        const current = parseFloat(this.heightInput.value);
        const base = isFinite(current) && current > 0 ? current : this.minH;
        this.setSizeFromHeight(base + delta);
      }
      this.finalizeSizeUpdate();
    }

    onSizeFieldEdit(axis) {
      if (!this.hasGenerated || this.isSyncingProportion) return;
      if (axis === 'width') {
        if (this.widthInput.value === '') return;
        this.setSizeFromWidth(this.widthInput.value);
      } else {
        if (this.heightInput.value === '') return;
        this.setSizeFromHeight(this.heightInput.value);
      }
      this.finalizeSizeUpdate();
    }

    finalizeSizeUpdate() {
      this.enforceMinQty();
      this.updatePriceUI();
      this.refreshCartLock();

      if (this.canAddToCart()) {
        this.setStatus('success', 'Size set. Price updated — you can add to cart.');
      } else if (this.hasGenerated) {
        const blockReason = this.getCartBlockReason();
        if (blockReason) {
          this.setStatus('error', blockReason);
        } else {
          this.setStatus('', 'Enter desired width or height (proportion stays locked).');
        }
      }
    }

    getPieces() {
      return this.enforceMinQty();
    }

    getSize() {
      const width = parseFloat(this.widthInput.value);
      const height = parseFloat(this.heightInput.value);
      if (
        !isFinite(width) ||
        !isFinite(height) ||
        width < this.minW ||
        height < this.minH ||
        width > this.maxW ||
        height > this.maxH
      ) {
        return null;
      }
      return {
        width: this.roundToStep(width),
        height: this.roundToStep(height),
      };
    }

    getPricing() {
      const size = this.getSize();
      if (!size) return null;

      const squareInches = size.width * size.height;
      const unitPrice = squareInches * this.rate;
      const pieces = this.getPieces();
      const subtotal = unitPrice * pieces;
      const discountPercent = this.getVolumeDiscountPercent(pieces, unitPrice);
      const discountAmount = subtotal * (discountPercent / 100);
      const lineTotal = subtotal - discountAmount;
      const cartCentsQty = Math.max(1, Math.round(lineTotal * 100));

      return {
        width: size.width,
        height: size.height,
        squareInches: squareInches,
        unitPrice: unitPrice,
        pieces: pieces,
        subtotal: subtotal,
        discountPercent: discountPercent,
        discountAmount: discountAmount,
        lineTotal: lineTotal,
        cartCentsQty: cartCentsQty,
      };
    }

    canAddToCart() {
      return Boolean(
        this.hasGenerated &&
          this.generatedImageUrl &&
          this.meetsMinimumOrder(this.getPricing())
      );
    }

    refreshCartLock() {
      this.lockCart(!this.canAddToCart());
    }

    updatePriceUI() {
      const pricing = this.getPricing();
      if (!pricing) {
        this.sqinEl.textContent = '—';
        this.unitPriceEl.textContent = '—';
        if (this.discountEl) this.discountEl.textContent = '—';
        if (this.discountRowEl) this.discountRowEl.hidden = true;
        this.lineTotalEl.textContent = '—';
        this.updateQuantityDiscountUI(null);
        this.updateProductPriceDisplay(null);
        return;
      }

      this.sqinEl.textContent = pricing.squareInches.toFixed(2);
      this.unitPriceEl.textContent = this.formatMoney(pricing.unitPrice);
      if (this.discountRowEl && this.discountEl) {
        if (pricing.discountPercent > 0) {
          this.discountRowEl.hidden = false;
          this.discountEl.textContent =
            pricing.discountPercent + '% (−' + this.formatMoney(pricing.discountAmount) + ')';
        } else {
          this.discountRowEl.hidden = true;
          this.discountEl.textContent = '—';
        }
      }
      this.lineTotalEl.textContent = this.formatMoney(pricing.lineTotal);
      this.updateQuantityDiscountUI(pricing);
      this.updateProductPriceDisplay(pricing.unitPrice);
      this.syncCartQuantity();
      this.writeFormProperties();
    }

    updateProductPriceDisplay(_unitPrice) {
      this.hideStorefrontPrice();
    }

    syncCartQuantity() {
      const pricing = this.getPricing();
      if (!this.cartQtyInput) return;
      if (!pricing) {
        this.cartQtyInput.value = '1';
        return;
      }
      // Function mode: Shopify qty = real pieces (Cart Transform sets unit price)
      // Cents mode: Shopify qty = line total in cents (legacy, shows 1350 on checkout)
      if (this.pricingMode === 'function') {
        this.cartQtyInput.value = String(Math.max(1, pricing.pieces));
      } else {
        this.cartQtyInput.value = String(pricing.cartCentsQty);
      }
    }

    onFileChange(event) {
      const file = event.target.files && event.target.files[0];
      this.clearGeneratedState();

      if (!file) {
        this.selectedFile = null;
        this.aspectRatio = null;
        this.imagePixelWidth = null;
        this.imagePixelHeight = null;
        this.sizeUsesArtwork = false;
        this.updateImageDimsUI();
        this.setGenerateEnabled(false);
        this.setCropEnabled(false);
        this.originalCard.hidden = true;
        this.originalImg.removeAttribute('src');
        this.setStatus('', 'Upload an image to generate an embroidery preview.');
        this.refreshCartLock();
        return;
      }

      if (!file.type || !file.type.startsWith('image/')) {
        this.selectedFile = null;
        this.fileInput.value = '';
        this.setGenerateEnabled(false);
        this.setCropEnabled(false);
        this.setStatus('error', 'Please choose an image file (PNG, JPG, etc.).');
        this.refreshCartLock();
        return;
      }

      this.selectedFile = file;
      this.setCropEnabled(true);
      const objectUrl = URL.createObjectURL(file);
      this.originalImg.onload = () => {
        this.readDimensionsFromUploadedImage(this.originalImg);
        URL.revokeObjectURL(objectUrl);
        if (!this.canGeneratePreview()) {
          this.setGenerateEnabled(false);
          this.refreshPreviewQuotaStatus();
          return;
        }
        this.setStatus(
          '',
          'Image ready (' +
            this.imagePixelWidth +
            '×' +
            this.imagePixelHeight +
            ' px). Click “Generate Embroidery”.' +
            (this.dailyLimitEnabled ? ' (' + this.getRemainingPreviews() + ' left today)' : '')
        );
      };
      this.originalImg.src = objectUrl;
      this.originalCard.hidden = false;

      if (!this.canGeneratePreview()) {
        this.setGenerateEnabled(false);
        this.refreshPreviewQuotaStatus();
      } else {
        this.setGenerateEnabled(true);
        this.setStatus('', 'Reading image dimensions…');
      }
      this.refreshCartLock();
    }

    clearGeneratedState() {
      this.originalKey = null;
      this.originalImageUrl = null;
      this.generatedImageUrl = null;
      this.cutoutSourceUrl = null;
      this.fulfillmentImageUrl = null;
      this.trimmedFulfillmentUrl = '';
      this.artworkImage = null;
      this.sizeUsesArtwork = false;
      this.generatedKey = null;
      this.cutoutId = null;
      this.hasGenerated = false;
      this.generatedCard.hidden = true;
      this.generatedImg.removeAttribute('src');
      this.generatedImg.classList.remove('embroidery-generator__img--blend');
      if (this.generatedCard) this.generatedCard.classList.remove('is-cleaned');
      this.sizePanel.hidden = true;
      this.updatePriceUI();
    }

    async onGenerate(event) {
      event.preventDefault();
      if (!this.selectedFile || this.isGenerating) return;

      if (!this.canGeneratePreview()) {
        this.setStatus('error', this.dailyLimitMessage());
        this.setGenerateEnabled(false);
        return;
      }

      this.isGenerating = true;
      this.userCancelled = false;
      this.setGenerateEnabled(false);
      this.setCropEnabled(false);
      this.lockCart(true);
      this.clearGeneratedState();
      this.originalCard.hidden = false;
      this.progressValue = 0;
      this.showProgress('Uploading…');
      this.startProgressAnimation(18, 4000);

      try {
        this.setStatus('loading', 'Preparing and uploading image…');
        const uploadFile = await this.prepareUploadFile(this.selectedFile);
        if (this.userCancelled) return;
        this.setProgressPercent(Math.max(this.progressValue, 12));
        if (this.progressLabelEl) this.progressLabelEl.textContent = 'Uploading…';

        const upload = await this.uploadImageFile(uploadFile);
        if (this.userCancelled) return;
        this.originalKey = upload.key;
        // Cart thumb is built from _Original Image key in Liquid (no base64 on the order)
        this.originalImageUrl = this.buildCartThumbnailUrl(upload.key) || upload.publicUrl || '';

        this.setProgressPercent(Math.max(this.progressValue, 22));
        if (this.progressLabelEl) this.progressLabelEl.textContent = 'Generating…';
        this.startProgressAnimation(90, 50000);
        this.setStatus(
          'loading',
          'Generating embroidery preview… Usually 30–60 seconds. Please keep this page open.'
        );
        const result = await this.generateEmbroidery(upload.key);
        if (this.userCancelled) return;
        await this.finishProgress();

        // Raw Cutout may include fabric plate — clean for preview + fulfillment download
        this.cutoutSourceUrl = result.imageUrl;
        this.generatedKey = result.generatedKey;
        this.cutoutId = result.id != null ? String(result.id) : '';
        this.hasGenerated = true;
        // Prefer durable Lambda-proxied URL (private S3 direct links = AccessDenied)
        this.fulfillmentImageUrl =
          result.fulfillmentUrl ||
          this.buildCartThumbnailUrl(result.generatedKey) ||
          '';
        this.generatedImageUrl = this.fulfillmentImageUrl || this.cutoutSourceUrl;

        // Show Cutout result immediately — bright & correct (no multiply blend)
        this.generatedImg.classList.remove('embroidery-generator__img--blend');
        this.generatedCard.classList.remove('is-cleaned');
        this.generatedImg.src = result.imageUrl;
        this.generatedCard.hidden = false;
        this.sizePanel.hidden = false;

        // Prefer server counter; fall back to local increment if Lambda has no quota yet
        if (!result.quota) this.consumePreviewQuota();
        else this.applyQuotaFromServer(result.quota);

        try {
          await this.applySizeFromArtwork(result.imageUrl);
        } catch (sizeError) {
          console.warn('[embroidery-generator] artwork size skipped', sizeError);
          this.sizeUsesArtwork = false;
          this.readDimensionsFromUploadedImage(this.originalImg);
          this.applyDefaultProportionalSize();
        }
        this.enforceMinQty();
        this.writeFormProperties();
        this.updatePriceUI();
        this.refreshCartLock();

        try {
          this.sizePanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        } catch (e) {
          // ignore
        }

        const sizeLabel =
          this.widthInput.value && this.heightInput.value
            ? this.widthInput.value + '" × ' + this.heightInput.value + '"'
            : 'proportional size';
        this.setStatus(
          'success',
          this.sizeUsesArtwork
            ? 'Preview ready. Print size follows the artwork after the background is removed (' +
              sizeLabel +
              '). Width and height stay in proportion. Use Crop if you want a different area.'
            : 'Preview ready. Size from your image: ' +
              sizeLabel +
              '. Width and height stay in proportion.'
        );

        this.isGenerating = false;
        this.setGenerateEnabled(Boolean(this.selectedFile) && this.canGeneratePreview());
        this.setCropEnabled(Boolean(this.selectedFile));
        this.refreshCartLock();
      } catch (error) {
        console.error('[embroidery-generator]', error);
        this.hideProgress();
        if (this.userCancelled) {
          this.setStatus('error', 'Generation cancelled. You can try again.');
        } else {
          if (error && error.quota) this.applyQuotaFromServer(error.quota);
          this.hasGenerated = false;
          this.sizePanel.hidden = true;
          this.lockCart(true);
          this.clearFormProperties();
          this.setStatus(
            'error',
            error && error.message
              ? error.message
              : 'Something went wrong. Please try again.'
          );
        }
      } finally {
        this.activeAbortController = null;
        this.hideProgress();
        this.isGenerating = false;
        this.setGenerateEnabled(Boolean(this.selectedFile) && this.canGeneratePreview());
        this.setCropEnabled(Boolean(this.selectedFile));
        if (!this.canGeneratePreview() && !this.hasGenerated) {
          this.refreshPreviewQuotaStatus();
        }
      }
    }

    /**
     * Upload via Lambda → S3 (avoids brittle browser PUT to S3 / CORS / resets).
     */
    async uploadImageFile(file) {
      const imageBase64 = await this.fileToBase64(file);
      const data = await this.postLambda(
        {
          action: 'upload-image',
          filename: file.name || 'upload.jpg',
          contentType: file.type || 'image/jpeg',
          imageBase64: imageBase64,
        },
        120000
      );

      const payload = data.data && typeof data.data === 'object' ? data.data : data;
      const key = payload.key || data.key;
      const publicUrl = payload.publicUrl || data.publicUrl || '';

      if (!key) {
        throw new Error('Upload failed: no object key returned.');
      }

      return { key: key, publicUrl: publicUrl || this.buildCartThumbnailUrl(key) || '' };
    }

    fileToBase64(file) {
      return new Promise(function (resolve, reject) {
        const reader = new FileReader();
        reader.onload = function () {
          const result = String(reader.result || '');
          const comma = result.indexOf(',');
          resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        reader.onerror = function () {
          reject(new Error('Could not read the image file.'));
        };
        reader.readAsDataURL(file);
      });
    }

    async getUploadUrl(file) {
      const data = await this.postLambda(
        {
          action: 'get-upload-url',
          filename: file.name || 'upload.png',
          contentType: file.type || 'application/octet-stream',
        },
        30000
      );

      const payload = data.data && typeof data.data === 'object' ? data.data : data;
      const uploadUrl = payload.uploadUrl || payload.url || data.uploadUrl || data.url;
      const key = payload.key || data.key;
      const publicUrl =
        payload.publicUrl ||
        payload.fileUrl ||
        payload.viewUrl ||
        data.publicUrl ||
        data.fileUrl ||
        this.buildPublicUploadUrl(uploadUrl, key);

      if (!uploadUrl || !key) {
        throw new Error('Upload URL was not returned by the server.');
      }

      return { uploadUrl: uploadUrl, key: key, publicUrl: publicUrl || '' };
    }

    /**
     * Prefer a small embedded JPEG data-URL (works without S3 public access).
     * Fall back to Lambda serve-upload proxy for larger artwork.
     */
    async prepareCartThumbnailUrl(file, key, publicUrl) {
      try {
        const dataUrl = await this.makeCartThumbnailDataUrl(file);
        // Keep well under typical Shopify line-item property size limits
        if (dataUrl && dataUrl.length > 32 && dataUrl.length < 12000) {
          return dataUrl;
        }
      } catch (e) {
        console.warn('[embroidery-generator] cart thumbnail compress failed', e);
      }
      return this.buildCartThumbnailUrl(key) || publicUrl || '';
    }

    makeCartThumbnailDataUrl(file) {
      return new Promise(function (resolve) {
        if (!file) {
          resolve('');
          return;
        }
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = function () {
          try {
            const maxSide = 140;
            let w = img.naturalWidth || 1;
            let h = img.naturalHeight || 1;
            const scale = Math.min(1, maxSide / Math.max(w, h));
            w = Math.max(1, Math.round(w * scale));
            h = Math.max(1, Math.round(h * scale));
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              resolve('');
              return;
            }
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL('image/jpeg', 0.62));
          } catch (err) {
            resolve('');
          } finally {
            URL.revokeObjectURL(objectUrl);
          }
        };
        img.onerror = function () {
          URL.revokeObjectURL(objectUrl);
          resolve('');
        };
        img.src = objectUrl;
      });
    }

    /**
     * Cart thumbnail URL that works with a private S3 bucket.
     * Requires Lambda action "serve-upload" (see LAMBDA_serve-upload-snippet.js).
     */
    buildCartThumbnailUrl(key) {
      if (!key || !this.lambdaUrl) return '';
      let normalized = String(key).trim();
      if (normalized.indexOf('uploads/') >= 0) {
        normalized =
          'uploads/' + normalized.split('uploads/').pop().split('?')[0].split('&')[0];
      } else if (normalized.indexOf('generated/') >= 0) {
        normalized =
          'generated/' + normalized.split('generated/').pop().split('?')[0].split('&')[0];
      } else {
        return '';
      }
      const base = this.lambdaUrl.replace(/\/$/, '');
      // Short path URLs stay clickable in Shopify admin
      if (normalized.indexOf('generated/') === 0) {
        return base + '/g/' + encodeURIComponent(normalized.slice('generated/'.length));
      }
      return base + '/u/' + encodeURIComponent(normalized.slice('uploads/'.length));
    }

    /**
     * Build a stable HTTPS URL for the uploaded artwork (fallback only).
     * Prefer stripping the query string from the S3 presigned PUT URL.
     */
    buildPublicUploadUrl(uploadUrl, key) {
      if (uploadUrl) {
        try {
          const u = new URL(uploadUrl);
          if (u.protocol === 'https:' || u.protocol === 'http:') {
            return u.origin + u.pathname;
          }
        } catch (e) {
          // fall through
        }
      }
      if (this.s3PublicBase && key) {
        return this.s3PublicBase + '/' + String(key).replace(/^\//, '');
      }
      return '';
    }

    async putToS3(uploadUrl, file) {
      const controller = new AbortController();
      const timer = setTimeout(function () {
        controller.abort();
      }, 60000);

      try {
        const response = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
          body: file,
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error('Failed to upload image to storage (' + response.status + ').');
        }
      } catch (error) {
        if (error && error.name === 'AbortError') {
          throw new Error('Image upload timed out. Please try again.');
        }
        throw error;
      } finally {
        clearTimeout(timer);
      }
    }

    async generateEmbroidery(key) {
      const identity = this.getQuotaIdentityPayload();
      const data = await this.postLambda(
        {
          action: 'generate-embroidery',
          key: key,
          clientId: identity.clientId,
          customerId: identity.customerId,
          dailyLimitEnabled: identity.dailyLimitEnabled,
          dailyLimit: identity.dailyLimit,
        },
        GENERATION_TIMEOUT_MS
      );

      const payload = data.data && typeof data.data === 'object' ? data.data : data;
      const imageUrl = payload.imageUrl || payload.image_url;
      const status = payload.status;
      const quota = data.quota || payload.quota || null;
      if (quota) this.applyQuotaFromServer(quota);

      if (data.success === false || status === 0) {
        throw new Error(
          payload.failReason || data.message || data.msg || 'Embroidery generation failed.'
        );
      }

      if (!imageUrl) {
        throw new Error('Embroidery generation did not return an image.');
      }

      return {
        imageUrl: String(imageUrl).trim(),
        id: payload.id,
        generatedKey: payload.generatedKey || payload.generated_key || null,
        fulfillmentUrl:
          payload.fulfillmentUrl ||
          payload.fulfillment_url ||
          null,
        quota: quota,
      };
    }

    async postLambda(body, timeoutMs) {
      const controller = new AbortController();
      this.activeAbortController = controller;
      const timer = setTimeout(function () {
        controller.abort();
      }, timeoutMs);

      try {
        const response = await fetch(this.lambdaUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        let json = null;
        try {
          json = await response.json();
        } catch (e) {
          json = null;
        }

        if (!response.ok) {
          const msg =
            (json && (json.message || json.msg || json.error)) ||
            'Request failed (' + response.status + ').';
          const err = new Error(msg);
          err.status = response.status;
          if (json && json.quota) err.quota = json.quota;
          throw err;
        }

        if (json && json.success === false) {
          const err = new Error(json.message || json.msg || json.error || 'Request failed.');
          if (json.quota) err.quota = json.quota;
          throw err;
        }

        return json || {};
      } catch (error) {
        if (error && error.name === 'AbortError') {
          if (this.userCancelled) {
            throw new Error('Generation cancelled. You can try again.');
          }
          throw new Error(
            'The request timed out. Generation usually takes 30–60 seconds, but can take longer on mobile. Please keep the page open and try again.'
          );
        }
        throw error;
      } finally {
        clearTimeout(timer);
        if (this.activeAbortController === controller) {
          this.activeAbortController = null;
        }
      }
    }

    ensurePropertyInput(form, name) {
      let input = form.querySelector('input[name="' + name + '"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }
      return input;
    }

    writeFormProperties() {
      const form = this.getProductForm();
      if (!form) return;

      // Admin needs clickable https links; Liquid extracts keys from these URLs for cart thumbs.
      const originalUrl = this.buildCartThumbnailUrl(this.originalKey) || this.originalKey || '';
      const apiUrl =
        this.buildCartThumbnailUrl(this.generatedKey) ||
        this.safePropertyUrl(this.fulfillmentImageUrl) ||
        '';
      const fulfillmentUrl = this.safePropertyUrl(this.trimmedFulfillmentUrl) || apiUrl;

      this.ensurePropertyInput(form, 'properties[_Original Image]').value = originalUrl;
      this.ensurePropertyInput(form, 'properties[_Generated Embroidery]').value =
        fulfillmentUrl;
      this.ensurePropertyInput(form, 'properties[_Fulfillment Image]').value =
        fulfillmentUrl;
      // Cutout CDN URLs are long + expire — keep ID only for support.
      this.ensurePropertyInput(form, 'properties[_Cutout Source]').value = '';
      this.ensurePropertyInput(form, 'properties[_Cutout.Pro ID]').value =
        this.cutoutId || '';

      if (this.generatedKey) {
        this.ensurePropertyInput(form, 'properties[_Generated Image Key]').value =
          this.generatedKey;
      }

      const pricing = this.getPricing();
      if (pricing) {
        this.ensurePropertyInput(form, 'properties[Desired size]').value =
          pricing.width + ' x ' + pricing.height + ' inch';
        this.ensurePropertyInput(form, 'properties[Price each]').value =
          this.formatMoney(pricing.unitPrice);
        if (pricing.discountPercent > 0) {
          this.ensurePropertyInput(form, 'properties[Volume discount]').value =
            pricing.discountPercent + '%';
        } else {
          this.ensurePropertyInput(form, 'properties[Volume discount]').value = '';
        }
        // Visible on checkout/admin so staff see real piece count
        this.ensurePropertyInput(form, 'properties[Quantity (pcs)]').value =
          String(pricing.pieces) + ' pcs';
        this.ensurePropertyInput(form, 'properties[_Rate per sq in]').value =
          String(this.rate);
        this.ensurePropertyInput(form, 'properties[_Min order]').value =
          String(this.minOrderAmount);
        this.ensurePropertyInput(form, 'properties[_Volume discount tiers]').value =
          JSON.stringify(this.volumeDiscountTiers);
        this.ensurePropertyInput(form, 'properties[_Square inches]').value =
          pricing.squareInches.toFixed(4);
        this.ensurePropertyInput(form, 'properties[_Pieces]').value = String(pricing.pieces);
        this.ensurePropertyInput(form, 'properties[_Line total]').value =
          this.formatMoney(pricing.lineTotal);
        // Do not save _Cart qty cents on the order — it confuses staff (1350 = price cents, not pieces)
        if (this.imagePixelWidth && this.imagePixelHeight) {
          this.ensurePropertyInput(form, 'properties[_Image pixels]').value =
            this.imagePixelWidth + ' x ' + this.imagePixelHeight;
        }
      }
    }

    clearFormProperties() {
      const form = this.getProductForm();
      if (!form) return;
      [
        'properties[_Original Image]',
        'properties[_Cart Thumbnail]',
        'properties[_Generated Embroidery]',
        'properties[_Fulfillment Image]',
        'properties[_Cutout Source]',
        'properties[_Cutout.Pro ID]',
        'properties[_Generated Image Key]',
        'properties[Desired size]',
        'properties[Price each]',
        'properties[Volume discount]',
        'properties[Quantity (pcs)]',
        'properties[_Rate per sq in]',
        'properties[_Min order]',
        'properties[_Volume discount tiers]',
        'properties[_Square inches]',
        'properties[_Pieces]',
        'properties[_Line total]',
        'properties[_Image pixels]',
      ].forEach(function (name) {
        const input = form.querySelector('input[name="' + name + '"]');
        if (input) input.value = '';
      });
    }
  }

  function boot() {
    document.querySelectorAll('[data-embroidery-generator]').forEach(function (root) {
      if (root.dataset.embroideryReady === 'true') return;
      root.dataset.embroideryReady = 'true';
      new EmbroideryGenerator(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
